import { CalendarIcon } from "lucide-react";
import * as React from "react";
import type { Matcher } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
	id?: string;
	value?: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	disableWeekends?: boolean;
	minDate?: string;
	maxDate?: string;
	disabledDays?: Matcher | Matcher[];
	className?: string;
	"aria-describedby"?: string;
	"aria-invalid"?: boolean;
	"aria-label"?: string;
}

function parseDateValue(value?: string): Date | undefined {
	if (!value) return undefined;
	const parsed = new Date(`${value}T00:00:00`);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function DatePicker({
	id,
	value,
	onChange,
	placeholder = "Pick a date",
	disabled,
	disableWeekends = false,
	minDate,
	maxDate,
	disabledDays: disabledDaysProp,
	className,
	...buttonProps
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);

	const date = parseDateValue(value);
	const minDateValue = parseDateValue(minDate);
	const maxDateValue = parseDateValue(maxDate);

	const handleSelect = (selected: Date | undefined) => {
		if (selected) {
			const yyyy = selected.getFullYear();
			const mm = String(selected.getMonth() + 1).padStart(2, "0");
			const dd = String(selected.getDate()).padStart(2, "0");
			onChange(`${yyyy}-${mm}-${dd}`);
		} else {
			onChange("");
		}
		setOpen(false);
	};

	const disabledDays = React.useMemo(() => {
		const matchers: Matcher[] = [];

		if (disableWeekends) {
			matchers.push((day: Date) => day.getDay() === 0 || day.getDay() === 6);
		}
		if (minDateValue) {
			matchers.push({ before: minDateValue });
		}
		if (maxDateValue) {
			matchers.push({ after: maxDateValue });
		}
		if (disabledDaysProp) {
			if (Array.isArray(disabledDaysProp)) {
				matchers.push(...disabledDaysProp);
			} else {
				matchers.push(disabledDaysProp);
			}
		}

		return matchers.length > 0 ? matchers : undefined;
	}, [disableWeekends, disabledDaysProp, maxDateValue, minDateValue]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					disabled={disabled}
					className={cn(
						// Field-shaped trigger: match Input's neutral fill.
						"bg-muted hover:bg-muted w-full justify-start text-left font-normal h-9",
						!value && "text-muted-foreground",
						className,
					)}
					{...buttonProps}
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					{date
						? date.toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
								year: "numeric",
							})
						: placeholder}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					mode="single"
					selected={date}
					onSelect={handleSelect}
					defaultMonth={date}
					disabled={disabledDays}
				/>
			</PopoverContent>
		</Popover>
	);
}
